import { Button, InlayPanel, Panel, Separator } from '@sorrem-client/ui/components';
import { useAppNavigate, useSelectedCharacterId } from '@sorrem-client/ui/hooks';
import { resolveApiErrorMessage, useJoinCrucibleMutation } from '@sorrem-client/ui/store';
import { resolveVeilSaturationById } from '@sorrem/shared';
import { useState } from 'react';
import { useCharacterHomeCharacter } from '@sorrem-client/ui/features/CharacterHome/components/CharacterHomeItemTransfer';
import type { CatalogCrucible } from '@sorrem-client/ui/features/CharacterHome/hooks';
import { resolveCrucibleTraits } from '../../crucible-traits';
import { CrucibleTraitBadge } from '../CrucibleTraitBadge';
import type { CrucibleCreateDraft } from '../CreateCruciblePanel';
import { CrucibleCreatePreview } from './CrucibleCreatePreview';
import styles from './CrucibleInfoPanel.module.scss';
import { toCrucibleAccentStyle, toCruciblePath } from '@sorrem-client/ui/features/CharacterHome/crucible-links';
import { toTourTargetProps } from '@sorrem-client/ui/shared/tour-targets';

interface CrucibleInfoPanelProps {
    crucible: CatalogCrucible | null;
    /** Why a linked crucible (an invite, #216) can't be joined, shown in place of its details. */
    unavailableNotice?: string | null;
    /** While the create form is open, what it would make: previewed instead of the selection (#224). */
    createDraft?: CrucibleCreateDraft | null;
}

/**
 * Details of the selected crucible: its description, its traits for the
 * character (what a party wipe would cost them, how its enemies fight),
 * and enter button. While the create form is open, a preview of the
 * crucible it would make instead (#224).
 */
export function CrucibleInfoPanel({ crucible, unavailableNotice = null, createDraft = null }: CrucibleInfoPanelProps): JSX.Element {
    const navigate = useAppNavigate();
    const { selectedCharacterId } = useSelectedCharacterId();
    const [joinCrucible, { isLoading }] = useJoinCrucibleMutation();
    const characterReadiness = useCharacterHomeCharacter()?.readiness ?? null;
    const traits = crucible !== null ? resolveCrucibleTraits(crucible, characterReadiness) : [];

    // Why entering failed, for the crucible it failed on: picking another crucible leaves it behind.
    const [joinError, setJoinError] = useState<{ crucibleId: string; message: string } | null>(null);
    const shownJoinError = joinError !== null && joinError.crucibleId === crucible?.id ? joinError.message : null;

    const handleEnter = async (): Promise<void> => {
        if (!crucible) return;
        setJoinError(null);
        try {
            await joinCrucible({ crucibleId: crucible.id, characterId: selectedCharacterId ?? undefined }).unwrap();
            navigate(toCruciblePath(crucible.id));
        } catch (failure) {
            // It filled up or began meanwhile, say: stay here, saying so.
            setJoinError({ crucibleId: crucible.id, message: resolveApiErrorMessage(failure, 'That crucible could not be entered. Try again.') });
        }
    };

    return (
        <Panel className={styles.crucibleInfoPanel}>
            {/* Scrolls on its own so the Enter button below never moves. */}
            <div className={styles.body} {...toTourTargetProps('crucible-info')}>
                <h2 className={styles.heading}>Crucible Info</h2>

                {createDraft !== null ? (
                    <CrucibleCreatePreview draft={createDraft} />
                ) : crucible === null ? (
                    <p className={styles.emptyNote} role={unavailableNotice !== null ? 'status' : undefined}>
                        {unavailableNotice ?? 'Select a crucible to see its details.'}
                    </p>
                ) : (
                    <>
                        <InlayPanel
                            className={styles.banner}
                            style={toCrucibleAccentStyle(crucible.accentColor)}
                        >
                            <p className={styles.crucibleName}>{crucible.name}</p>
                            <p className={styles.crucibleTier}>{resolveVeilSaturationById(crucible.veilSaturation).displayName}</p>
                        </InlayPanel>

                        <p className={styles.description}>{crucible.description}</p>

                        <Separator />

                        <section className={styles.section}>
                            <h3 className={styles.subheading}>Traits</h3>
                            <ul className={styles.traitList}>
                                {traits.map((trait) => (
                                    <li key={trait.id} className={styles.trait}>
                                        <CrucibleTraitBadge trait={trait} hasTooltip={false} />
                                        <span className={styles.traitText}>
                                            <span className={styles.traitLabel}>{trait.label}</span>
                                            <span className={styles.traitDescription}>{trait.description}</span>
                                        </span>
                                    </li>
                                ))}
                            </ul>
                        </section>
                    </>
                )}
            </div>

            {createDraft === null && crucible !== null && (
                <div className={styles.footer}>
                    {shownJoinError !== null && <p className={styles.error} role="alert">{shownJoinError}</p>}
                    <Button
                        disabled={isLoading}
                        onClick={() => { void handleEnter(); }}
                    >
                        Enter
                    </Button>
                </div>
            )}
        </Panel>
    );
}
